import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, ArrowRight } from "lucide-react";

/**
 * ProformaCTA — Phase 5.3 (revised) entrypoint.
 *
 * The calculator now creates a matter DIRECTLY and redirects to the client
 * portal. The pro-forma becomes an artefact INSIDE the matter (a checklist
 * task the client accepts from the portal).
 *
 * POST /api/matter/bootstrap → navigate to /client/:portal_token
 */
export default function ProformaCTA({
  calcInputs,
  matterType = "conveyancing",
  label = "Open your matter",
  variant = "primary",
  testid = "proforma-cta",
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // OTP-2 fix: skip pro-forma gate — bootstrap the matter directly and
      // land the user in their private portal. The pro-forma is now a task
      // INSIDE the portal (not a blocking page on the public path).
      const { data } = await api.post("/matter/bootstrap", {
        ...form,
        ...calcInputs,
        matter_type: matterType,
      });
      toast.success("Your matter is open — taking you to your portal");
      navigate(data.portal_url || `/client/${data.portal_token}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setForm({ name: "", email: "", phone: "" });
  };

  const btnClass =
    variant === "primary"
      ? "bg-[#0055FF] hover:bg-[#003ECC] text-white px-5 py-3 text-sm font-medium"
      : variant === "ghost"
      ? "border border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 px-3 py-2 text-xs"
      : "bg-white text-zinc-900 hover:bg-zinc-100 px-5 py-3 text-sm font-medium";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`${btnClass} transition-colors inline-flex items-center gap-2`}
        data-testid={testid}
      >
        {label} <ArrowRight className={variant === "ghost" ? "h-3 w-3" : "h-4 w-4"} />
      </button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="rounded-none" data-testid="proforma-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-overline text-zinc-500">
              <FileText className="h-3 w-3" /> Open your matter
            </div>
            <DialogTitle className="font-serif text-3xl">Let&apos;s get started</DialogTitle>
            <DialogDescription>
              We&apos;ll open your file and your private client portal. Everything happens inside the portal —
              upload your OTP, accept the pro-forma (managed or assisted pricing), and track each step.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="pf-name">Full name</Label>
              <Input
                id="pf-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none"
                data-testid="proforma-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pf-email">Email</Label>
                <Input
                  id="pf-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-none"
                  data-testid="proforma-email"
                />
              </div>
              <div>
                <Label htmlFor="pf-phone">Phone</Label>
                <Input
                  id="pf-phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-none"
                  data-testid="proforma-phone"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800"
              data-testid="proforma-submit"
            >
              {loading ? "Opening your matter…" : "Open my matter →"}
            </Button>
            <p className="text-[10px] text-zinc-500 leading-relaxed">
              You&apos;ll land directly in your private portal — no extra steps. Your pro-forma uses a
              deterministic pricing engine (SARS transfer duty + Deeds Office fees are exact; the
              legal/admin portion shows a min/max range) and is waiting inside the portal.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
